/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/AppointmentRequestSections/Documents/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { useState } from "react";
import styles from "./Documents.module.css";
import { useTranslation } from "react-i18next";
import FileUploadArea from "../../../modals/ProposalCreateModal/FileUploadArea";
import { createFileObjectFromUrl } from "../../../utils/common";
import { VALIDATION_ERROR_LABEL } from "../../../utils/formValidationConstants.js";

const Documents = ({ company, getFieldValidation }) => {
  const { t } = useTranslation();

  const isTablet = window.innerWidth <= 1023;

  const [brochureFile, setBrochureFile] = useState(
    createFileObjectFromUrl(company?.brochure),
  );
  const [taxIDFile, setTaxIDFile] = useState(
    createFileObjectFromUrl(company?.cedulaFiscal),
  );
  const [positiveComplianceOpinionFile, setPositiveComplianceOpinionFile] =
    useState(createFileObjectFromUrl(company?.opinionDeCumplimientoPositiva));
  const [civilLiabilityPolicyFile, setCivilLiabilityPolicyFile] = useState(
    createFileObjectFromUrl(company?.polizaDeResponsabilidadCivil),
  );
  const [IMSSRegistrationFile, setIMSSRegistrationFile] = useState(
    createFileObjectFromUrl(company?.altaIMSS),
  );
  const [REPSEFile, setREPSEFile] = useState(
    createFileObjectFromUrl(company?.REPSE),
  );
  const [proofOfCertificationFile, setProofOfCertificationFile] = useState(
    createFileObjectFromUrl(company?.comprobanteDeCertificacion),
  );

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.documents.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={styles.inputRow}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.documents.brochureLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={brochureFile}
              isUploading={false}
              isDragging={false}
              error={
                getFieldValidation && getFieldValidation("brochure")
                  ? VALIDATION_ERROR_LABEL
                  : ""
              }
              onFileChange={() => {}}
              onDragOver={() => {}}
              onDragLeave={() => {}}
              onDrop={() => {}}
              onClick={() => {}}
              reduceHeightForTablet={isTablet}
              disabled={true}
            />
            <input
              id="brochureFileInput"
              type="file"
              className={styles.fileInput}
              style={{ display: "none" }}
              disabled={true}
            />
          </div>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.documents.taxIDLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={taxIDFile}
              isUploading={false}
              isDragging={false}
              error={
                getFieldValidation && getFieldValidation("cedulaFiscal")
                  ? VALIDATION_ERROR_LABEL
                  : ""
              }
              onFileChange={() => {}}
              onDragOver={() => {}}
              onDragLeave={() => {}}
              onDrop={() => {}}
              onClick={() => {}}
              reduceHeightForTablet={isTablet}
              disabled={true}
            />
            <input
              id="taxIDFileInput"
              type="file"
              disabled={true}
              className={styles.fileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.documents.positiveComplianceOpinionLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={positiveComplianceOpinionFile}
              isUploading={false}
              isDragging={false}
              error={
                getFieldValidation &&
                getFieldValidation("opinionDeCumplimientoPositiva")
                  ? VALIDATION_ERROR_LABEL
                  : ""
              }
              onFileChange={() => {}}
              onDragOver={() => {}}
              onDragLeave={() => {}}
              onDrop={() => {}}
              onClick={() => {}}
              reduceHeightForTablet={isTablet}
              disabled={true}
            />
            <input
              id="positiveComplianceOpinionFileInput"
              type="file"
              disabled={true}
              className={styles.fileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>
      <div className={styles.inputRow}>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.documents.civilLiabilityPolicyLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={civilLiabilityPolicyFile}
              isUploading={false}
              isDragging={false}
              error={
                getFieldValidation &&
                getFieldValidation("polizaDeResponsabilidadCivil")
                  ? VALIDATION_ERROR_LABEL
                  : ""
              }
              onFileChange={() => {}}
              onDragOver={() => {}}
              onDragLeave={() => {}}
              onDrop={() => {}}
              onClick={() => {}}
              reduceHeightForTablet={isTablet}
              disabled={true}
            />
            <input
              id="civilLiabilityPolicyFileInput"
              type="file"
              disabled={true}
              className={styles.fileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.documents.IMSSRegistrationLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={IMSSRegistrationFile}
              isUploading={false}
              isDragging={false}
              error={
                getFieldValidation && getFieldValidation("altaIMSS")
                  ? VALIDATION_ERROR_LABEL
                  : ""
              }
              onFileChange={() => {}}
              onDragOver={() => {}}
              onDragLeave={() => {}}
              onDrop={() => {}}
              onClick={() => {}}
              reduceHeightForTablet={isTablet}
              disabled={true}
            />
            <input
              id="IMSSRegistrationFileInput"
              type="file"
              disabled={true}
              className={styles.fileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.documents.REPSELabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={REPSEFile}
              isUploading={false}
              isDragging={false}
              error={
                getFieldValidation && getFieldValidation("REPSE")
                  ? VALIDATION_ERROR_LABEL
                  : ""
              }
              onFileChange={() => {}}
              onDragOver={() => {}}
              onDragLeave={() => {}}
              onDrop={() => {}}
              onClick={() => {}}
              reduceHeightForTablet={isTablet}
              disabled={true}
            />
            <input
              id="REPSEFileInput"
              type="file"
              disabled={true}
              className={styles.fileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>
      <div className={styles.inputRow}>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.documents.proofOfCertificationLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={proofOfCertificationFile}
              isUploading={false}
              isDragging={false}
              error={
                getFieldValidation &&
                getFieldValidation("comprobanteDeCertificacion")
                  ? VALIDATION_ERROR_LABEL
                  : ""
              }
              onFileChange={() => {}}
              onDragOver={() => {}}
              onDragLeave={() => {}}
              onDrop={() => {}}
              onClick={() => {}}
              reduceHeightForTablet={isTablet}
              disabled={true}
            />
            <input
              id="proofOfCertificationFileInput"
              type="file"
              disabled={true}
              className={styles.fileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;
